import React from "react";
import { Navigate } from "react-router-dom";
import { PeekBox, PeekStack } from "@piiqtechnologies/ui-components";
import { usePolicyContext } from "@piiqtechnologies/ui-lib";
import { useBillingEnabled } from "@hooks";
import { buildOrgPath, PATHS } from "@constants/Paths";
import { useOptionalActiveOrganization } from "@stores/ActiveOrganizationContext";
import { useAuthContext } from "@stores/AuthContext";
import { useOptionalPlansBillingContext } from "@stores/PlansBillingContext";
import { getIsAdmin } from "@stores/utils";
import { DiscoverAction } from "@constants/Policies";
const PlansBilling = React.lazy(() => import("@pages/plans-billing"));
const AccountSettings = React.lazy(() => import("@pages/account-settings"));
export const DashboardLayout = ({ banner, sidebar, children, contentRef, pageHeader, }) => {
    const billingEnabled = useBillingEnabled();
    const plansBilling = useOptionalPlansBillingContext();
    const showPlansBilling = billingEnabled && Boolean(plansBilling === null || plansBilling === void 0 ? void 0 : plansBilling.isPlansBillingOpen);
    const showAccountSettings = Boolean(plansBilling === null || plansBilling === void 0 ? void 0 : plansBilling.isAccountSettingsOpen);
    // Both overlays are opened by a `?view=` param, so the URL alone can ask for them — from a
    // bookmark, a shared link, or simply by switching organizations while one is open. The param is
    // not a capability, and the capabilities it needs are per-organization: PlansBillingProvider
    // mounts above ActiveOrgLayout and can only see the account-level role, whereas this layout
    // renders below it and sees the role for the organization on screen. So a user who administers
    // one organization but only reads another is caught here.
    //
    // Withheld means gone, not blank: leaving the underlying page rendered beneath a settings or
    // billing query string strands the user somewhere they cannot act, so they go home instead. The
    // redirect also runs before either overlay mounts, so a viewer never fires the billing page's
    // fetches.
    const { user } = useAuthContext();
    const activeOrgContext = useOptionalActiveOrganization();
    const canManageUsers = usePolicyContext(DiscoverAction.ManageUsers);
    const canManageBilling = usePolicyContext(DiscoverAction.ManageBilling);
    const homePath = getIsAdmin(user)
        ? PATHS.ADMIN_DASHBOARD
        : activeOrgContext
            ? buildOrgPath(activeOrgContext.activeOrgId, PATHS.HOME)
            : PATHS.RESTRICTED;
    if ((showAccountSettings && !canManageUsers) || (showPlansBilling && !canManageBilling)) {
        return React.createElement(Navigate, { to: homePath, replace: true });
    }
    return (React.createElement(PeekStack, { width: "100%", height: "100vh", direction: "column", justifyContent: "space-between" },
        React.createElement(PeekStack, { direction: "row", sx: { width: "100%", flex: 1, minHeight: 0 } },
            sidebar,
            React.createElement(PeekBox, { ref: contentRef, sx: {
                    p: 2,
                    flex: 1,
                    overflowY: "auto",
                    backgroundColor: "grey.50",
                    display: "flex",
                    flexDirection: "column",
                } }, showAccountSettings ? (React.createElement(AccountSettings, null)) : showPlansBilling ? (React.createElement(PlansBilling, null)) : (React.createElement(PeekStack, { gap: 3, sx: { flex: 1, minHeight: 0 } }, pageHeader ? (React.createElement(PeekStack, { gap: 2 },
                banner && banner,
                pageHeader,
                children)) : (React.createElement(PeekStack, { gap: 2, sx: { flex: 1, minHeight: 0 } },
                banner && banner,
                children))))))));
};
export default DashboardLayout;
